import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Clock, Coffee, Delete, LogIn, LogOut, ShieldAlert, UserX, ArrowLeft, Play, Briefcase,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PAUSA_LABEL } from "@/lib/fichajeUi";
import { Button } from "@/components/ui/button";

const ACCION_LABEL = {
  entrada: { titulo: "Entrada", icon: LogIn, color: "bg-green-600 hover:bg-green-700" },
  salida: { titulo: "Salida", icon: LogOut, color: "bg-zinc-600 hover:bg-zinc-700" },
  pausa: { titulo: "Pausa", icon: Coffee, color: "bg-amber-600 hover:bg-amber-700" },
  ausencia: { titulo: "Ausencia", icon: UserX, color: "bg-violet-600 hover:bg-violet-700" },
  vuelta: { titulo: "Volver de pausa", icon: Play, color: "bg-sky-600 hover:bg-sky-700" },
};

export default function Quiosco() {
  const { token } = useParams();
  const [empresa, setEmpresa] = useState(null);
  const [invalido, setInvalido] = useState(false);
  const [pin, setPin] = useState("");
  const [reloj, setReloj] = useState(new Date());
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [trabajador, setTrabajador] = useState(null);
  const [pinActivo, setPinActivo] = useState("");
  const [vista, setVista] = useState("pin"); // pin | acciones | pausa | confirmar_ausencia | proyecto
  const [motivosPausa, setMotivosPausa] = useState([]);
  const [alcanceAusencia, setAlcanceAusencia] = useState("tramo");
  const [motivoAusencia, setMotivoAusencia] = useState("");

  useEffect(() => {
    api
      .get(`/quiosco/${token}/info`)
      .then((r) => setEmpresa(r.data.empresa))
      .catch(() => setInvalido(true));
  }, [token]);

  useEffect(() => {
    const t = setInterval(() => setReloj(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const reset = useCallback(() => {
    setPin("");
    setPinActivo("");
    setTrabajador(null);
    setVista("pin");
    setError("");
  }, []);

  const identificar = useCallback(
    async (pinCompleto) => {
      setEnviando(true);
      setError("");
      try {
        const { data } = await api.post(`/quiosco/${token}/identificar`, { pin: pinCompleto });
        setPinActivo(pinCompleto);
        setTrabajador(data);
        setMotivosPausa(data.motivos_pausa || []);
        setVista("acciones");
        setPin("");
      } catch (e) {
        setError(formatApiError(e));
        setTimeout(() => setError(""), 3000);
        setPin("");
      } finally {
        setEnviando(false);
      }
    },
    [token]
  );

  const fichar = useCallback(
    async (accion, opts = {}) => {
      const esStr = typeof opts === "string";
      const motivo = esStr ? opts : opts.motivo;
      const proyecto_id = !esStr ? opts.proyecto_id : undefined;
      const tarea_id = !esStr ? opts.tarea_id : undefined;
      const ruta_comercial_id = !esStr ? opts.ruta_comercial_id : undefined;
      const alcance_ausencia = !esStr ? opts.alcance_ausencia : undefined;
      const motivo_ausencia = !esStr ? opts.motivo_ausencia : undefined;
      setEnviando(true);
      setError("");
      try {
        const payload = { pin: pinActivo, accion };
        if (motivo) payload.motivo = motivo;
        if (proyecto_id) payload.proyecto_id = proyecto_id;
        if (tarea_id) payload.tarea_id = tarea_id;
        if (ruta_comercial_id) payload.ruta_comercial_id = ruta_comercial_id;
        if (opts.alcance_ausencia) payload.alcance_ausencia = opts.alcance_ausencia;
        if (opts.motivo_ausencia) payload.motivo_ausencia = opts.motivo_ausencia;
        const { data } = await api.post(`/quiosco/${token}/fichar`, payload);
        setResultado(data);
        setVista("pin");
        setTrabajador(null);
        setPinActivo("");
        setTimeout(() => setResultado(null), 5000);
      } catch (e) {
        setError(formatApiError(e));
        setTimeout(() => setError(""), 4000);
      } finally {
        setEnviando(false);
      }
    },
    [token, pinActivo]
  );

  const pulsar = (d) => {
    if (enviando || resultado || vista !== "pin") return;
    setError("");
    const nuevo = (pin + d).slice(0, 4);
    setPin(nuevo);
    if (nuevo.length === 4) identificar(nuevo);
  };

  const borrar = () => {
    if (vista === "pin") setPin(pin.slice(0, -1));
  };

  const elegirAccion = (accion, opts = {}) => {
    if (accion === "pausa") {
      setVista("pausa");
      return;
    }
    if (accion === "ausencia") {
      setVista("confirmar_ausencia");
      return;
    }
    if (accion === "entrada" && (trabajador?.proyectos_asignados?.length || 0) > 0) {
      setVista("proyecto");
      return;
    }
    fichar(accion, opts);
  };

  if (invalido) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-zinc-950 text-white" data-testid="quiosco-invalido">
        <ShieldAlert className="h-10 w-10 text-red-400" strokeWidth={1.5} />
        <p className="text-lg">Quiosco no válido o desactivado</p>
        <p className="text-sm text-zinc-500">Pide al administrador un nuevo enlace de quiosco.</p>
      </div>
    );
  }

  const mensajeResultado = () => {
    if (!resultado) return null;
    const tipo = resultado.tipo || resultado.accion;
    if (tipo === "entrada") {
      const proy = resultado.proyecto_codigo;
      return proy
        ? `Entrada en ${proy} registrada a las ${resultado.hora}`
        : `Entrada registrada a las ${resultado.hora}`;
    }
    if (tipo === "salida") return `Salida registrada a las ${resultado.hora}`;
    if (tipo === "pausa") return `Pausa registrada a las ${resultado.hora}`;
    if (tipo === "vuelta") return `Vuelta registrada a las ${resultado.hora}`;
    if (tipo === "ausencia") {
      if (resultado.extra?.includes("pendiente_aprobacion")) {
        const alcance = resultado.extra?.includes("alcance_dia_completo") ? "día completo" : "tramo actual";
        return `Solicitud de ausencia (${alcance}) enviada al encargado`;
      }
      if (resultado.extra?.includes("salida_programada")) {
        return `Ausencia registrada. Salida automática a las ${resultado.hora}`;
      }
      return `Ausencia registrada. Salida a las ${resultado.hora}`;
    }
    return `Registro a las ${resultado.hora}`;
  };

  const extraRetro = resultado?.extra?.includes("entrada_retroactiva");

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-zinc-950 px-6 py-8 text-white" data-testid="quiosco-page">
      <div className="flex items-center gap-2 text-zinc-400">
        <Clock className="h-4 w-4" strokeWidth={1.5} />
        <span className="text-sm">{empresa || "Cargando..."}</span>
      </div>
      <p className="font-display mt-2 text-5xl font-light tracking-tight" data-testid="quiosco-reloj">
        {reloj.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
      </p>

      {resultado ? (
        <div
          className={`mt-10 flex w-full max-w-sm flex-col items-center gap-3 rounded-2xl p-8 ${
            resultado.dentro || resultado.en_pausa ? "bg-green-600" : "bg-zinc-700"
          }`}
          data-testid="quiosco-resultado"
        >
          <p className="font-display text-2xl font-medium">¡Hola, {resultado.nombre.split(" ")[0]}!</p>
          <p className="text-center text-sm opacity-90">{mensajeResultado()}</p>
          {extraRetro && (
            <p className="text-center text-xs opacity-80">
              Se registró también una entrada retroactiva por olvido de picado.
            </p>
          )}
          {resultado.extra?.includes("salida_programada") && (
            <p className="text-center text-xs opacity-80">
              La salida se registrará automáticamente al fin de tu jornada.
            </p>
          )}
        </div>
      ) : vista === "acciones" && trabajador ? (
        <div className="mt-8 w-full max-w-sm space-y-4" data-testid="quiosco-acciones">
          <div className="text-center">
            <p className="text-sm text-zinc-400">Hola,</p>
            <p className="font-display text-2xl font-medium">{trabajador.nombre.split(" ")[0]}</p>
            <p className="mt-1 text-xs text-zinc-500">
              {trabajador.en_pausa
                ? "Estás en pausa"
                : trabajador.dentro
                  ? "En jornada"
                  : "Sin fichar"}
            </p>
          </div>
          <div className="grid gap-3">
            {trabajador.acciones.map((accion) => {
              const cfg = ACCION_LABEL[accion];
              if (!cfg) return null;
              const Icon = cfg.icon;
              return (
                <Button
                  key={accion}
                  disabled={enviando}
                  onClick={() => elegirAccion(accion)}
                  className={`h-14 w-full rounded-2xl text-base font-medium text-white ${cfg.color}`}
                  data-testid={`quiosco-accion-${accion}`}
                >
                  <Icon className="mr-2 h-5 w-5" strokeWidth={1.5} />
                  {cfg.titulo}
                </Button>
              );
            })}
          </div>
          <Button variant="ghost" className="w-full text-zinc-400" onClick={reset} disabled={enviando}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Cancelar
          </Button>
        </div>
      ) : vista === "pausa" ? (
        <div className="mt-8 w-full max-w-sm space-y-4" data-testid="quiosco-pausa">
          <p className="text-center text-sm text-zinc-400">Motivo de la pausa</p>
          <div className="grid grid-cols-2 gap-3">
            {(motivosPausa.length ? motivosPausa : Object.entries(PAUSA_LABEL).map(([id, label]) => ({ id, label }))).map(
              (m) => (
                <Button
                  key={m.id}
                  disabled={enviando}
                  onClick={() => fichar("pausa", { motivo: m.id })}
                  className="h-14 rounded-2xl bg-amber-600 text-sm hover:bg-amber-700"
                >
                  {m.label || PAUSA_LABEL[m.id]}
                </Button>
              )
            )}
          </div>
          <Button variant="ghost" className="w-full text-zinc-400" onClick={() => setVista("acciones")} disabled={enviando}>
            Volver
          </Button>
        </div>
      ) : vista === "proyecto" ? (
        <div className="mt-8 w-full max-w-sm space-y-4" data-testid="quiosco-proyecto">
          <div className="flex items-center justify-center gap-2 text-violet-300">
            <Briefcase className="h-4 w-4" />
            <p className="text-center text-sm font-medium">Asignar entrada a proyecto</p>
          </div>
          <p className="text-center text-xs text-zinc-500">
            Elige un proyecto o continúa sin asignar.
          </p>
          <Button
            disabled={enviando}
            onClick={() => fichar("entrada")}
            className="h-12 w-full rounded-2xl bg-green-600 hover:bg-green-700"
            data-testid="quiosco-sin-proyecto-btn"
          >
            <LogIn className="mr-2 h-4 w-4" />
            Entrar sin proyecto
          </Button>
          <p className="text-center text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            o selecciona un proyecto
          </p>
          <div className="grid gap-3">
            {(trabajador?.proyectos_asignados || []).map((p) => (
              <div key={p.id} className="space-y-1">
              <Button
                disabled={enviando}
                onClick={() =>
                  fichar(
                    "entrada",
                    p.es_ruta ? { ruta_comercial_id: p.ruta_comercial_id } : { proyecto_id: p.id },
                  )
                }
                data-testid={`quiosco-proyecto-${p.id}`}
                className="h-auto min-h-14 w-full justify-start rounded-2xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-left hover:bg-violet-950 hover:border-violet-700"
              >
                <span
                  className="mr-3 h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: p.color || "#6366f1" }}
                />
                <span className="min-w-0">
                  <span className="block text-[10px] font-semibold uppercase tracking-wide text-zinc-500">{p.codigo}</span>
                  <span className="block truncate text-sm font-medium text-white">{p.nombre}</span>
                </span>
              </Button>
              {p.tareas?.map((t) => (
                <Button
                  key={t.id}
                  disabled={enviando}
                  onClick={() => fichar("entrada", { proyecto_id: p.id, tarea_id: t.id })}
                  className="ml-6 h-auto min-h-10 w-[calc(100%-1.5rem)] justify-start rounded-xl border border-violet-800 bg-violet-950/50 px-4 py-2 text-left text-sm hover:bg-violet-900"
                >
                  {p.codigo || p.nombre} · {t.codigo ? `${t.codigo} · ` : ""}{t.nombre}
                </Button>
              ))}
              </div>
            ))}
          </div>
          <Button variant="ghost" className="w-full text-zinc-400" onClick={() => setVista("acciones")} disabled={enviando}>
            Volver
          </Button>
        </div>
      ) : vista === "confirmar_ausencia" ? (
        <div className="mt-8 w-full max-w-sm space-y-4 rounded-2xl border border-violet-800 bg-violet-950/40 p-6" data-testid="quiosco-ausencia">
          <p className="text-center text-sm font-medium text-violet-100">Ausencia justificada</p>
          <p className="text-center text-xs leading-relaxed text-violet-200/80">
            Se enviará la solicitud a tu encargado para su aprobación.
          </p>
          <div className="space-y-2 text-sm">
            <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-violet-800 p-3 has-[:checked]:border-violet-500 has-[:checked]:bg-violet-900/50">
              <input
                type="radio"
                name="quiosco-alcance"
                checked={alcanceAusencia === "tramo"}
                onChange={() => setAlcanceAusencia("tramo")}
                className="mt-1"
              />
              <span>
                <span className="font-medium text-violet-50">Solo tramo actual</span>
                <span className="mt-0.5 block text-xs text-violet-200/70">Salida al fin de este tramo</span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-violet-800 p-3 has-[:checked]:border-violet-500 has-[:checked]:bg-violet-900/50">
              <input
                type="radio"
                name="quiosco-alcance"
                checked={alcanceAusencia === "dia_completo"}
                onChange={() => setAlcanceAusencia("dia_completo")}
                className="mt-1"
              />
              <span>
                <span className="font-medium text-violet-50">Día completo</span>
                <span className="mt-0.5 block text-xs text-violet-200/70">Todos los tramos del día</span>
              </span>
            </label>
          </div>
          <textarea
            value={motivoAusencia}
            onChange={(e) => setMotivoAusencia(e.target.value)}
            rows={2}
            placeholder="Motivo (opcional)"
            className="w-full rounded-xl border border-violet-800 bg-violet-950/60 px-3 py-2 text-sm text-violet-50 placeholder:text-violet-300/50"
          />
          <Button
            disabled={enviando}
            onClick={() =>
              fichar("ausencia", {
                alcance_ausencia: alcanceAusencia,
                motivo_ausencia: motivoAusencia.trim(),
              })
            }
            className="h-12 w-full rounded-2xl bg-violet-600 hover:bg-violet-700"
          >
            Enviar solicitud
          </Button>
          <Button variant="ghost" className="w-full text-zinc-400" onClick={() => setVista("acciones")} disabled={enviando}>
            Volver
          </Button>
        </div>
      ) : (
        <>
          <p className="mt-8 text-sm text-zinc-400">Introduce tu PIN</p>

          <div className="mt-4 flex gap-3" data-testid="quiosco-pin-dots">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className={`h-4 w-4 rounded-full border-2 transition-colors ${
                  pin.length > i ? "border-white bg-white" : "border-zinc-600"
                }`}
              />
            ))}
          </div>

          {error && (
            <p className="mt-3 rounded-lg bg-red-500/15 px-4 py-1.5 text-sm text-red-400" data-testid="quiosco-error">
              {error}
            </p>
          )}

          <div className="mt-8 grid w-full max-w-xs grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <button
                key={n}
                onClick={() => pulsar(String(n))}
                data-testid={`quiosco-tecla-${n}`}
                className="font-display rounded-2xl border border-zinc-800 bg-zinc-900 py-5 text-2xl font-light transition-colors active:bg-zinc-700"
              >
                {n}
              </button>
            ))}
            <span />
            <button
              onClick={() => pulsar("0")}
              data-testid="quiosco-tecla-0"
              className="font-display rounded-2xl border border-zinc-800 bg-zinc-900 py-5 text-2xl font-light transition-colors active:bg-zinc-700"
            >
              0
            </button>
            <button
              onClick={borrar}
              data-testid="quiosco-borrar"
              className="flex items-center justify-center rounded-2xl border border-zinc-800 bg-zinc-900 py-5 transition-colors active:bg-zinc-700"
            >
              <Delete className="h-6 w-6 text-zinc-400" strokeWidth={1.5} />
            </button>
          </div>
        </>
      )}

      <p className="mt-10 text-xs text-zinc-600">Ventura HR · Modo quiosco</p>
    </div>
  );
}
